// src/pages/UcatPracticeExam.tsx
import React, { useState, useReducer, useEffect, useRef, useCallback } from "react";
import { useLocation, useNavigate, useBlocker } from "react-router-dom";
import {
  examReducer,
  initialState,
} from "../../../Components/PracticeTestReducer/TestReducer";
import {
  SplitPane,
  SinglePageQuestion,
  NavigatorModal,
  ExplanationModal,
  ScrollWarningModal,
  SubmitExamModal,
  ReloadWarningModal,
  EndExamConfirmationModal,
  UcatSectionReview,
  EndReviewConfirmationModal,
  TimeExpiredModal,
  NoFlaggedModal,
} from "../../../Components/Practice_Test/Exam_component";
import { UcatLayout } from "../../../Components/Shared";
import { Calculator } from "../../../Components/Calculator/Calculator";
import {
  ChevronLeft,
  ChevronRight,
  HelpCircle,
  Flag,
  Layers,
  LogOut,
  LayoutGrid,
  ArrowLeft,
  ArrowRight,
  PenLine,
} from "lucide-react";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { listExamID } from "../../../../../Redux/Slices/Exam/ListExamIDSlice";
import { addOpenSection } from "../../../../../Redux/Slices/OpenSection/AddOpenSectionSlice";
import { addCloseSection } from "../../../../../Redux/Slices/Exam/AddCloseSectionSlice";
import { listStudentExam } from "../../../../../Redux/Slices/StudentExam/ListStudentExamSlice";
import { EditStudentExam } from "../../../../../Redux/Slices/StudentExam/EditStudentExamSlice";
import CircularProgress from "@mui/material/CircularProgress";
import { AnswerType, ExamSection, Question } from "../../../Components/sample data/Type";
import { useAppDispatch, useAppSelector } from "../../../../../Redux/Store/Hooks";
import {
  addExamAnswer,
} from "../../../../../Redux/Slices/ExamAnswer/AddExamAnswerSlice";
import { listExamAnswer } from "../../../../../Redux/Slices/ExamAnswer/ListExamAnswerSlice";
import { EditExamAnswer } from "../../../../../Redux/Slices/ExamAnswer/EditExamAnswerSlice";
import { listExamsubmit } from "../../../../../Redux/Slices/ExamAnswer/ListExamsubmitSlice";
import { pendingExam } from "../../../../../Redux/Slices/PendingExam/PendingExamSlice";
import { addFalgExam } from "../../../../../Redux/Slices/FalgExam/AddFalgExamSlice";
import { listFlagExam } from "../../../../../Redux/Slices/FalgExam/ListFlagExamSlice";
import { listReExamPracticeID } from "../../../../../Redux/Slices/ReExamPractice/ListReExamPracticeSlice";
import useNavigationBlocker from "../../../../../Hooks/useNavigationBlocker";

// ReactQuill Configuration
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

const FontSize = ReactQuill.Quill.import("formats/size");
FontSize.whitelist = ["small", false, "large", "huge"];
ReactQuill.Quill.register(FontSize, true);

const modules = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["blockquote"],
  ],
  history: {
    delay: 500,
    maxStack: 100,
    userOnly: true,
  },
};

const formats = [
  "bold",
  "italic",
  "underline",
  "strike",
  "script",
  "list",
  "bullet",
  "indent",
  "direction",
  "blockquote",
  "align",
];

// Helper/
const parseDurationToSeconds = (durationStr: string | null): number => {
  if (!durationStr) return 0;
  const match = durationStr.match(/(\d+)/);
  return match ? parseInt(match[0], 10) * 60 : 0;
};

const UcatPracticeExam = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useAppDispatch();

  // Enable browser navigation blocker
  useNavigationBlocker(true);

  dayjs.extend(utc);
  dayjs.extend(timezone);
  const offsetMinutes = dayjs().tz(dayjs.tz.guess()).utcOffset();

  // Get data from Redux
  const { data: listExamIDSlice, isLoading } = useAppSelector(
    (state) => state.ListExamIDSlice
  );

  const { data: pendingExamSlice, isLoading: isPendingExamLoading } = useAppSelector(
    (state) => state.PendingExamSlice
  );

  const { data: reExamPracticeSlice, isLoading: isReExamLoading } = useAppSelector(
    (state: any) => state.ListReExamPracticeIDSlice
  );

  const { data: skipexamdata, isLoading: isSkipExamLoading } = useAppSelector(
    (state: any) => state.EditStudentExamSlice
  );


  // Determine if we are in a resume/reload situation. 
  // If we have data in listExamIDSlice but it might be from a previous section/reload,
  // we want to prioritize the fresh results from pendingExam (ResumeExam API).
  const isResuming = useRef(!location.state?.isNavigation); // If no navigation state, assume reload/resume

  const pendingData = Array.isArray(pendingExamSlice?.data)
    ? pendingExamSlice?.data[0]
    : pendingExamSlice?.data;

  const reExamData = Array.isArray(reExamPracticeSlice?.data)
    ? reExamPracticeSlice?.data[0]
    : reExamPracticeSlice?.data;

  // CRITICAL: For pending UCAT exams, use start API (listExamIDSlice) instead of resume API (pendingData)
  // Resume API should only trigger at the end of the exam.
  // FIX: If Retake mode using retake_id, strictly use reExamData to avoid picking up stale Resume/Start data.
  // FIX: If Retake mode using retake_id, prefer reExamData but fallback to listExamIDSlice if reExamData is missing.
  const isResumeMode = location.state?.isResume || sessionStorage.getItem("ucat_exam_mode") === "pending";
  const isRetakeMode = location.state?.isRetake || sessionStorage.getItem("ucat_exam_mode") === "retake";

  // Determine effective exam data based on mode.
  // CRITICAL FIX: If we have listExamIDSlice (Start API) data, fulfill it regardless of 'isRetakeMode' flag.
  // The 'isRetakeMode' prioritizing reExamData causes issues on reload because reExamData is empty but listExamIDSlice is fetched effectively.
  const examData = (
    // 1. If we have fresh start data, use it (covers Retake reload scenario where we call listExamID)
    listExamIDSlice?.data ||
    // 2. If Retake mode and we have retake data (initial flow)
    (isRetakeMode ? reExamData : null) ||
    // 3. Fallbacks
    pendingData ||
    reExamData
  );

  console.log("DEBUG: examMode (session)", sessionStorage.getItem("ucat_exam_mode"));
  console.log("DEBUG: isRetakeMode", isRetakeMode);
  console.log("DEBUG: pendingExamSlice", pendingExamSlice);
  console.log("DEBUG: pendingData", pendingData);
  console.log("DEBUG: final examData", examData);

  // Determine effective section index
  const effectiveSectionIndex = location.state?.sectionIndex !== undefined
    ? location.state.sectionIndex
    : (sessionStorage.getItem("ucat_last_section_index") ? parseInt(sessionStorage.getItem("ucat_last_section_index")!) : 0);

  const sectionIndex = effectiveSectionIndex;
  const effectiveQuestionIndex = location.state?.questionIndex ?? 0;

  const resumeFetchedRef = useRef(false);
  const examLoadedRef = useRef(false);

  // Use API data or fallback to empty array to prevent crash
  const rawSections = examData?.questions_by_type || [];

  // Robust section identification for reload scenarios
  const rawSection = (() => {
    // 1. Try matching by EXPLICIT ID in navigation state
    if (location.state?.question_type_id) {
      const found = rawSections.find((s: any) => s.question_type_id === location.state.question_type_id);
      if (found) return found;
    }

    // 2. If we are explicitly navigating with an index, trust that index over any stored data
    if (location.state?.sectionIndex !== undefined && rawSections.length > location.state.sectionIndex) {
      return rawSections[location.state.sectionIndex];
    }

    // 3. Only if it's a cold reload (no state), try matching by stored ID
    if (!location.state) {
      const storedId = sessionStorage.getItem("ucat_last_section_id") ? parseInt(sessionStorage.getItem("ucat_last_section_id")!) : undefined;
      if (storedId) {
        const found = rawSections.find((s: any) => s.question_type_id === storedId);
        if (found) return found;
      }
    }

    // 4. Fallback to absolute index (works if rawSections is a full list)
    if (rawSections.length > effectiveSectionIndex) {
      return rawSections[effectiveSectionIndex];
    }

    // 5. Last resort fallback for Resume API: if it's a filtered list, take the first one
    if (pendingData && rawSections.length > 0) {
      return rawSections[0];
    }

    return null;
  })();

  // Persist sectionIndex and ID to handle reloads
  useEffect(() => {
    if (location.state?.sectionIndex !== undefined && location.state?.sectionIndex !== null) {
      sessionStorage.setItem("ucat_last_section_index", location.state.sectionIndex.toString());
    }
  }, [location.state?.sectionIndex]);

  useEffect(() => {
    if (rawSection?.question_type_id) {
      sessionStorage.setItem("ucat_last_section_id", rawSection.question_type_id.toString());
    }
  }, [rawSection?.question_type_id]);

  const normalizeQuestions = useCallback((questions: any[]): any => {
    if (!questions) return [];
    return questions.map((q) => {
      let finalOptions = (q.options || []).map((opt: any) => ({
        ...opt,
        option_id: opt.option_id || opt.id || null,
        option_text: opt.option_text || opt.option || opt.answer || null,
        option_url: opt.option_url || opt.image_url || null,
      }));

      if (q.answer_type === AnswerType.TRUE_FALSE && (!finalOptions || finalOptions.length === 0)) {
        finalOptions = [
          { option_id: -1, option_text: "Yes", option_url: null },
          { option_id: -2, option_text: "No", option_url: null },
        ];
      }

      return {
        question_id: q.question_id || q.id,
        question_text: q.question_text || q.question || null,
        question_url: q.question_url || q.image_url || null,
        prompt: q.prompt || null,
        prompt_url: q.prompt_url || null,
        imgWidth: q.imgWidth || null,
        prompt_imgWidth: q.prompt_imgWidth || null,
        instruction: q.instruction || null,
        options: finalOptions,
        answer_type: (() => {
          const type = String(q.answer_type || "").toUpperCase();
          if (type === "DRAG_DROP" || type === "DRAG-DROP") return AnswerType.DRAG_DROP;
          if (type === "TRUE_FALSE" || type === "TRUE-FALSE" || type === "YES_NO") return AnswerType.TRUE_FALSE;
          return AnswerType.MCQ;
        })(),
        drag_drop_data: q.drag_drop_data || null,
        drag_drop_pattern: q.drag_drop_pattern || null,
        correctAnswerIndex: q.correctAnswerIndex,
        explanation: q.explanation,
        subtype_name: q.subtype_name || null,
      };
    });
  }, []);

  const section: any = React.useMemo(() => {
    if (!rawSection) return { title: "Loading...", questions: [] };
    return {
      id: rawSection.question_type_id,
      title: rawSection.question_type_name,
      questions: normalizeQuestions(rawSection.questions),
      correctAnswerIndex: undefined,
    };
  }, [rawSection, normalizeQuestions]);

  const { data: listStudentExamSlice } = useAppSelector(
    (state) => state.ListStudentExamSlice
  );

  // Robustly get studentExamId. OnlineExam uses listStudentExamSlice?.data.
  // In some states it might be an array or an object.
  const ListStudentExam: any = listStudentExamSlice?.data;
  const studentExamId = React.useMemo(() => {
    const sessionStudentExamId = sessionStorage.getItem("ucat_student_exam_id");
    if (sessionStudentExamId) return sessionStudentExamId;

    let id = Array.isArray(ListStudentExam)
      ? (ListStudentExam[0]?.student_exam_id || ListStudentExam[0]?.id)
      : (ListStudentExam?.student_exam_id || ListStudentExam?.id);

    if (!id) {
      if (pendingData?.student_exam_id) id = pendingData.student_exam_id;
      else if (pendingData?.id) id = pendingData.id;
      else if (reExamData?.student_test_id) id = reExamData.student_test_id;
    }
    return id;
  }, [ListStudentExam, pendingData, reExamData]);

  console.log("🔍 listStudentExamSlice:", listStudentExamSlice);
  console.log("🔍 studentExamId (Resolved):", studentExamId);

  useEffect(() => {
    const passedExamId = location.state?.exam_id;
    if (passedExamId) {
      sessionStorage.setItem("ucat_exam_id", passedExamId);
    }

    // Only fetch once, never spam
    if (!studentExamId && !examLoadedRef.current) {
      examLoadedRef.current = true;
      console.log("📞 Fetching student exam ID");
      dispatch(listStudentExam());
    }
  }, [dispatch, studentExamId, location.state?.exam_id]);

  // Ensure studentExamId is always available
  useEffect(() => {
    const loadStudentExamId = async () => {
      if (!studentExamId) {
        console.log("📞 Fetching student exam ID...");
        try {
          await dispatch(listStudentExam());
        } catch (error) {
          console.error("Failed to load student exam ID:", error);
        }
      }
    };

    loadStudentExamId();
  }, [dispatch, studentExamId]);

  // Re-fetch Exam Questions if missing or if we are resuming (e.g., on reload)
  useEffect(() => {
    const state = location.state || {};

    // Do nothing until studentExamId is READY
    if (!studentExamId) return;

    // Prevent multiple resume/start calls
    if (resumeFetchedRef.current) return;

    // 👉 PRIORITY 1: RETAKE
    if (state.isRetake && state.retake_id && !state.startNow) {
      resumeFetchedRef.current = true;
      console.log("♻️ Retake mode");
      dispatch(listReExamPracticeID({
        student_test_id: state.retake_id,
        client_tz_offset: offsetMinutes
      }));
      return;
    }

    // 👉 PRIORITY 2: RESUME (PENDING)
    if (state.isResume || isResumeMode) {
      resumeFetchedRef.current = true;
      console.log("▶️ Resume exam for studentExamId:", studentExamId);
      dispatch(pendingExam({ id: Number(studentExamId) }));
      return;
    }

    // 👉 PRIORITY 3: START NEW
    if (state.startNow || (!listExamIDSlice?.data || !listExamIDSlice?.data.questions_by_type)) {
      resumeFetchedRef.current = true;
      console.log("▶️ Start new exam using studentExamId:", studentExamId);
      dispatch(listExamID({ stud_exam_id: Number(studentExamId) }));
      return;
    }
  }, [dispatch, studentExamId, location.state, pendingData]);

  // Pearson-style browser back-button trap
  useEffect(() => {
    // Initial push to ensure there's a state to "pop" back into without leaving the page
    window.history.pushState(null, "", window.location.href);

    const handlePopState = (e: PopStateEvent) => {
      // Immediately push back to "lock" the URL
      window.history.pushState(null, "", window.location.href);
      // Trigger the app's internal warning modal
      setShowReloadWarning(true);
    };

    window.addEventListener("popstate", handlePopState);
    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Handle Jump from Navigation State
  useEffect(() => {
    if (location.state?.questionIndex !== undefined) {
      dispatchReducer({ type: "JUMP_QUESTION", index: location.state.questionIndex });
    }
  }, [location.state?.questionIndex, effectiveSectionIndex]);

  const { data: addExamAnswerRes } = useAppSelector(
    (state) => state.AddExamAnswerSlice
  );
  const { data: editExamAnswerRes } = useAppSelector(
    (state) => state.EditExamAnswerSlice
  );

  const { data: listFlagRes } = useAppSelector(
    (state) => state.ListFlagExamSlice
  );
  const { data: addFlagRes } = useAppSelector(
    (state) => state.AddFalgExamSlice
  );

  // Section start logic moved to TestInstructionScreen.tsx to allow instruction screens between sections.
  // The following useEffect is removed to prevent duplicate section opens.
  /*
  useEffect(() => {
    if (studentExamId && section.id) {
      dispatch(
        addOpenSection({
          student_exam_id: studentExamId,
          remaining_time: null,
          closed: false,
          question_type_id: section.id,
        })
      );
    }
  }, [dispatch, studentExamId, section.id]);
  */

  const [state, dispatchReducer] = useReducer(examReducer, initialState); // Renamed dispatch to dispatchReducer
  const [showNavigator, setShowNavigator] = useState(false);
  const [showEndSection, setShowEndSection] = useState(false);
  const [showEndReview, setShowEndReview] = useState(false);
  const [showTimeExpiredModal, setShowTimeExpiredModal] = useState(false);
  const [showScrollWarning, setShowScrollWarning] = useState(false);
  const [scrollStatus, setScrollStatus] = useState({ left: true, right: true });
  const [isReviewState, setIsReviewState] = useState(false);
  const [hasReachedReview, setHasReachedReview] = useState(false);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isSkipping, setIsSkipping] = useState(false);
  const [showNoFlaggedPopup, setShowNoFlaggedPopup] = useState(false);

  const writingLeftScrollRef = useRef<HTMLDivElement>(null);
  const writingRightScrollRef = useRef<HTMLDivElement>(null);

  const checkWritingScroll = useCallback(() => {
    const buffer = 10;
    let newLeft = scrollStatus.left;
    let newRight = scrollStatus.right;

    if (!newLeft && writingLeftScrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = writingLeftScrollRef.current;
      if (scrollHeight <= clientHeight || scrollTop + clientHeight >= scrollHeight - buffer) {
        newLeft = true;
      }
    }

    if (!newRight && writingRightScrollRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = writingRightScrollRef.current;
      if (scrollHeight <= clientHeight || scrollTop + clientHeight >= scrollHeight - buffer) {
        newRight = true;
      }
    }

    if (newLeft !== scrollStatus.left || newRight !== scrollStatus.right) {
      setScrollStatus({ left: newLeft, right: newRight });
    }
  }, [scrollStatus]);

  const [questionStartTime, setQuestionStartTime] = useState<string | null>(null);

  // Writing interface state
  const [editorValue, setEditorValue] = useState<string>("");
  const [wordCount, setWordCount] = useState(0);
  const [characterCount, setCharacterCount] = useState(0);
  const [sentenceCount, setSentenceCount] = useState(0);
  const [paragraphCount, setParagraphCount] = useState(0);

  const [showEndExamConfirmation, setShowEndExamConfirmation] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);

  // Navigation Blocker Logic
  const [showReloadWarning, setShowReloadWarning] = useState(false);
  // Block internal navigation unless explicitly allowed (e.g. submit)
  // We'll use a ref or state to track if we're submitting vs just leaving
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  const blocker = useBlocker(
    ({ currentLocation, nextLocation, historyAction }) => {
      // 1. ALWAYS block browser back/forward buttons (POP)
      if (historyAction === "POP") {
        console.log("🚫 Blocking browser arrow navigation");
        return true;
      }

      // 2. Allow if we are explicitly submitting/skipping
      if (isSubmitting || isSubmittingRef.current) {
        console.log("✅ Allowing navigation (submitting/skipping)");
        return false;
      }

      // 3. Allow if the path isn't changing (e.g. hash changes, which we don't use but good for safety)
      if (currentLocation.pathname === nextLocation.pathname) {
        return false;
      }

      // 4. Specifically check allowed transition paths for programmatic navigation
      const allowedPaths = ["/ucat_exampractice_instruction", "/ucat_examresult_practice", "/ucat_test"];
      const isAllowedTarget = allowedPaths.includes(nextLocation.pathname);

      // Block everything else
      return !isAllowedTarget;
    }
  );

  const handleSkipExam = async (navigateAfter = true) => {
    console.log("🔄 handleSkipExam called");
    console.log("🔍 studentExamId:", studentExamId);

    if (!studentExamId || isNaN(Number(studentExamId))) {
      console.error("❌ Cannot skip exam: Invalid studentExamId", studentExamId);
      return;
    }

    // Set submitting flags IMMEDIATELY to prevent pop-ups during API call
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      // Get current question safely
      const currentQuestion = section.questions?.[state.currentQuestionIndex];
      const questionId = currentQuestion?.question_id || 0;

      console.log("📝 Current question:", currentQuestion);
      console.log("📝 Question ID:", questionId);

      // Wrap dispatch in a Promise to wait for Saga completion
      await new Promise<void>((resolve, reject) => {
        const payload = {
          student_exam_id: Number(studentExamId),
          pending_time: formatTime(state.timeRemaining),
          question_type_id: section?.id || 0,
          sentence: editorValue || "",
          question_id: questionId,
          onSuccess: () => {
            console.log("✅ Skip API successful (Callback)");
            resolve();
          },
          onFailure: (error: any) => {
            console.error("❌ Skip API failed (Callback):", error);
            reject(error);
          }
        };

        console.log("📤 Sending skip payload with callbacks:", payload);
        dispatch(EditStudentExam(payload));
      });

      // Clear session storage
      sessionStorage.removeItem("ucat_last_section_index");
      sessionStorage.removeItem("ucat_last_section_id");
      sessionStorage.removeItem("ucat_exam_id");

      if (navigateAfter) {
        // Use replace: true to prevent going back
        navigate("/ucat_test", { replace: true });
      }

    } catch (err) {
      console.error("❌ Skip API failed/rejected:", err);

      // Even if API fails, still navigate (optional - depends on requirements)
      if (navigateAfter) {
        console.log("⚠️ API failed, navigating anyway");
        navigate("/ucat_test", { replace: true });
      }
    } finally {
      setIsSkipping(false);
      // Reset submitting flags after a short delay to ensure navigation completes
      setTimeout(() => {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }, 1000);
    }
  };

  const handleSkipClick = async () => {
    setIsSkipping(true);
    try {
      await handleSaveAnswer();
      await handleSkipExam();
    } finally {
      setIsSkipping(false);
    }
  };

  const handleReloadConfirm = async () => {
    isSubmittingRef.current = true;
    // setIsSubmitting(true); // Driven by Redux isSkipExamLoading now

    // Save current progress before reload/navigation
    await handleSaveAnswer();
    await handleSkipExam(false); // Don't navigate, just save state

    // Logic to resume will be triggered by useEffect on remount after the reload

    if (blocker.state === "blocked") {
      navigate('/ucat_test');
      blocker.reset();
    } else {
      navigate('/ucat_test');
    }
    setShowReloadWarning(false);
  };

  const handleReloadCancel = () => {
    if (blocker.state === "blocked") {
      blocker.reset();
    }
    setShowReloadWarning(false);
  };

  // Fetch already submitted answers
  const { data: listExamAnswerSlice } = useAppSelector(
    (state) => state.ListExamAnswerSlice
  );
  const existingAnswers = listExamAnswerSlice?.data || [];

  useEffect(() => {
    if (!studentExamId || isNaN(Number(studentExamId))) {
      console.warn("🚫 Skipping listExamAnswer – invalid studentExamId:", studentExamId);
      return;
    }

    dispatch(listExamAnswer({ student_exam_id: Number(studentExamId) }));
  }, [dispatch, studentExamId, addExamAnswerRes, editExamAnswerRes]);

  useEffect(() => {
    if (studentExamId) {
      dispatch(listFlagExam({ student_exam_id: studentExamId }));
    }
  }, [dispatch, studentExamId, addFlagRes]);

  // Timer Effect
  useEffect(() => {
    if (rawSection?.duration && studentExamId) {
      const timerKey = `ucat_timer_${studentExamId}_${effectiveSectionIndex}`;
      const savedTime = sessionStorage.getItem(timerKey);

      const targetQuestionIndex = location.state?.questionIndex ?? 0;

      if (savedTime && parseInt(savedTime) > 0) {
        console.log(`⏱️ Resuming section ${effectiveSectionIndex} (Q: ${targetQuestionIndex}) from saved time: ${savedTime}s`);
        dispatchReducer({ type: "START_SECTION", time: parseInt(savedTime), index: targetQuestionIndex });
      } else {
        // Check for pending_time from server (Resume scenario)
        const pendingSeconds = parseDurationToSeconds((pendingData as any)?.pending_time);

        if (pendingSeconds > 0) {
          console.log(`⏱️ Resuming section ${effectiveSectionIndex} from server pending time: ${pendingSeconds}s`);
          dispatchReducer({ type: "START_SECTION", time: pendingSeconds, index: targetQuestionIndex });
        } else {
          const seconds = parseDurationToSeconds(rawSection.duration);
          console.log(`⏱️ Starting section ${effectiveSectionIndex} (Q: ${targetQuestionIndex}) with ${seconds} seconds`);
          dispatchReducer({ type: "START_SECTION", time: seconds, index: targetQuestionIndex });
        }
      }
    }
  }, [effectiveSectionIndex, rawSection?.duration, studentExamId, location.state?.questionIndex, pendingData]);

  useEffect(() => {
    const timer = setInterval(() => {
      dispatchReducer({ type: "TICK_TIMER" });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // Persistent Timer: Save to sessionStorage every time it ticks
  useEffect(() => {
    if (state.timeRemaining > 0 && studentExamId && rawSection?.duration) {
      const timerKey = `ucat_timer_${studentExamId}_${effectiveSectionIndex}`;
      sessionStorage.setItem(timerKey, state.timeRemaining.toString());
    }
  }, [state.timeRemaining, studentExamId, effectiveSectionIndex]);

  const timerStartedRef = useRef(false);

  // Reset timer started flag when section changes
  useEffect(() => {
    timerStartedRef.current = false;
  }, [effectiveSectionIndex]);

  useEffect(() => {
    if (state.timeRemaining > 0) {
      timerStartedRef.current = true;
    }
  }, [state.timeRemaining]);

  // Time-up logic
  useEffect(() => {
    if (timerStartedRef.current && state.timeRemaining === 0 && rawSection?.duration) {
      console.log("⏰ Time up! Showing expiry modal...");
      const timerKey = `ucat_timer_${studentExamId}_${effectiveSectionIndex}`;
      sessionStorage.removeItem(timerKey);
      setShowTimeExpiredModal(true);
    }
  }, [state.timeRemaining, rawSection?.duration, studentExamId, effectiveSectionIndex]);

  const formatTime = (totalSeconds: number) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Sync existing answers and flags to local reducer
  // We only care about syncing when server data changes or the section changes.
  // Using state.answers in dependency array can cause infinite loops or race conditions.
  useEffect(() => {
    if (section?.questions?.length > 0) {
      const answersMap: any = {};

      const rawFlagData: any = listFlagRes?.data;
      const flagArray = Array.isArray(rawFlagData) ? rawFlagData : (rawFlagData?.data || []);
      const flaggedMap = new Map<number, boolean>(
        flagArray?.filter((item: any) => item.flagged).map((item: any) => [Number(item.question_id || item.id), true])
      );

      const rawAnswerData: any = listExamAnswerSlice?.data;
      const answerArray = Array.isArray(rawAnswerData) ? rawAnswerData : (rawAnswerData?.data || []);

      // Prevent syncing stale answers from a different exam
      if (answerArray.length > 0) {
        const firstAnswer = answerArray[0];
        const answerExamId = Number(firstAnswer.student_exam_id || firstAnswer.student_test_id);
        const currentExamId = Number(studentExamId);

        if (!isNaN(answerExamId) && !isNaN(currentExamId) && answerExamId !== currentExamId) {
          // console.log("⚠️ Skipping sync of stale answers:", { answerExamId, currentExamId });
          return;
        }
      }

      section.questions.forEach((q: any) => {
        const questionId = q.question_id;
        const answer = answerArray.find((a: any) => Number(a.question_id || a.id) === Number(questionId));
        const isFlaggedFromServer = flaggedMap.has(Number(questionId));
        const currentLocal = state.answers[questionId];

        const isDragDrop = q.answer_type === AnswerType.DRAG_DROP;
        let serverDragDrop = null;
        if (isDragDrop && (answer?.answer_data || answer?.sentence)) {
          try {
            serverDragDrop = JSON.parse(answer.answer_data || answer.sentence);
          } catch (e) {
            console.error("Failed to parse drag drop answer", e);
          }
        }

        let serverOptionIndex = null;
        if (answer) {
          if (answer.option_id) {
            serverOptionIndex = q.options.findIndex((opt: any) => opt.option_id === answer.option_id);
          } else if (answer.answer !== undefined && answer.answer !== null) {
            // Handle cases where the server sends the answer text or a boolean directly
            const serverAnsStr = String(answer.answer).toLowerCase();
            serverOptionIndex = q.options.findIndex((opt: any) => {
              const optText = String(opt.option_text || "").toLowerCase();
              return optText === serverAnsStr ||
                (serverAnsStr === "true" && (optText === "yes" || optText === "true")) ||
                (serverAnsStr === "false" && (optText === "no" || optText === "false"));
            });
          }
        }
        const validServerIndex = serverOptionIndex !== null && serverOptionIndex !== -1;

        const finalIsFlagged = isFlaggedFromServer;

        const needsAnswerSync = !currentLocal || (
          isDragDrop
            ? (!currentLocal.dragDropAnswers && (serverDragDrop || answer?.answer_data || answer?.sentence))
            : (currentLocal.selectedOptionIndex === null && (validServerIndex || (answer?.sentence || answer?.answer_data)))
        );

        const needsFlagSync = !currentLocal || (currentLocal.isFlagged !== finalIsFlagged);

        // Ensure we don't sync if the server data is just a string "null" or "{}"
        const serverHasRealData = (validServerIndex) ||
          (answer?.sentence && answer.sentence !== "null" && answer.sentence !== "{}") ||
          (answer?.answer_data && answer.answer_data !== "null" && answer.answer_data !== "{}");

        if ((needsAnswerSync && serverHasRealData) || needsFlagSync) {
          answersMap[questionId] = {
            selectedOptionIndex: needsAnswerSync ? (validServerIndex ? serverOptionIndex : null) : currentLocal?.selectedOptionIndex,
            dragDropAnswers: isDragDrop ? (serverDragDrop || currentLocal?.dragDropAnswers) : currentLocal?.dragDropAnswers,
            sentence: (answer?.sentence && answer.sentence !== "null") ? answer.sentence : currentLocal?.sentence,
            answer_data: (answer?.answer_data && answer.answer_data !== "null") ? answer.answer_data : currentLocal?.answer_data,
            isFlagged: finalIsFlagged,
            isViewed: currentLocal?.isViewed || !!answer || isFlaggedFromServer,
          };
        }
      });

      if (Object.keys(answersMap).length > 0) {
        dispatchReducer({ type: "LOAD_EXISTING_ANSWERS", answers: answersMap } as any);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listExamAnswerSlice, section, listFlagRes]);

  const handleOptionSelect = (index: number) => {
    dispatchReducer({
      type: "SELECT_ANSWER",
      questionId: question.question_id,
      optionIndex: index,
    });
  };

  const handleDragDropChange = (itemId: number, bucketName: string | null, isExclusive?: boolean) => {
    dispatchReducer({
      type: "SET_DRAG_DROP_ANSWER",
      questionId: question.question_id,
      itemId,
      bucketName,
      isExclusive,
    });
  };

  const handleFlag = () => {
    if (studentExamId && question.question_id) {
      dispatch(
        addFalgExam({
          student_exam_id: studentExamId,
          question_id: question.question_id,
        })
      );
    }

    dispatchReducer({
      type: "TOGGLE_FLAG",
      questionId: question.question_id,
    });
  };

  const handleToggleFlag = (questionId: number) => {
    if (studentExamId && questionId) {
      dispatch(
        addFalgExam({
          student_exam_id: studentExamId,
          question_id: questionId,
        })
      );
    }

    dispatchReducer({
      type: "TOGGLE_FLAG",
      questionId: questionId,
    });
  };

  const handleClearAllFlags = () => {
    // Only clear flags for currently flagged questions in this section
    const flaggedQuestions = section.questions.filter((q: any) => state.answers[q.question_id]?.isFlagged);

    flaggedQuestions.forEach((q: any) => {
      if (studentExamId) {
        dispatch(
          addFalgExam({
            student_exam_id: studentExamId,
            question_id: q.question_id,
          })
        );
      }
      dispatchReducer({
        type: "TOGGLE_FLAG",
        questionId: q.question_id,
      });
    });
  };

  const handleEditorChange = (content: any) => {
    const textContent = content?.replace(/<[^>]+>/g, "")?.trim();
    const words = textContent?.split(/\s+/).filter((word: string) => word.length > 0);

    setEditorValue(content);
    setWordCount(words?.length || 0);

    const characterCount = textContent?.replace(/\s+/g, "")?.length || 0;
    setCharacterCount(characterCount);

    const sentences =
      textContent
        ?.split(/[.!?]+/)
        .filter((sentence: any) => sentence?.trim()?.length > 0) || [];
    setSentenceCount(sentences.length);

    const paragraphs =
      textContent
        ?.split(/\n+/)
        .filter((para: any) => para?.trim()?.length > 0) || [];
    setParagraphCount(paragraphs?.length || 0);
  };

  const showNotFlaggedPopup = () => {
    setShowNoFlaggedPopup(true);
  };

  const handleSaveAnswer = () => {
    console.log("💾 handleSaveAnswer called");
    console.log("💾 studentExamId:", studentExamId);
    console.log("💾 question:", question);
    console.log("💾 userAnswer:", userAnswer);

    if (!studentExamId || isNaN(Number(studentExamId)) || !question?.question_id) {
      console.warn("🚫 Save skipped – missing IDs", {
        studentExamId,
        questionId: question?.question_id
      });
      return;
    }

    const existingAnswer = existingAnswers.find(
      (ans: any) => ans.question_id === question.question_id
    );

    const selectedOption = question.options[userAnswer.selectedOptionIndex!];

    // Check if this is a writing question
    const isWritingQuestion = section.title === "Writing" || section.title?.toLowerCase().includes("writing");

    const isDragDrop = question.answer_type === AnswerType.DRAG_DROP;
    const isAnswered = isDragDrop
      ? userAnswer.dragDropAnswers && Object.values(userAnswer.dragDropAnswers).some((v) => v !== null)
      : !!selectedOption;

    const payload = {
      student_exam_id: studentExamId,
      question_id: question.question_id || (question as any).id,
      option_id: selectedOption?.option_id || (selectedOption as any)?.id || null,
      remaining_time: null,
      skipped: isWritingQuestion
        ? editorValue.trim() === ""
        : !isAnswered,
      question_type_id: section.id,
      question_start_time: questionStartTime || new Date().toISOString(),
      question_end_time: new Date().toISOString(),
      sentence: isWritingQuestion
        ? editorValue
        : isDragDrop && isAnswered
          ? JSON.stringify(userAnswer.dragDropAnswers)
          : null,
      answer_data: isDragDrop && isAnswered
        ? JSON.stringify(userAnswer.dragDropAnswers)
        : null,
    };

    console.log("Triggering save answer:", { isEdit: !!existingAnswer?.id, payload });

    if (existingAnswer?.id) {
      return dispatch(EditExamAnswer({ id: existingAnswer.id, ...payload }));
    } else {
      return dispatch(addExamAnswer(payload));
    }
  };

  const onNext = () => {
    // Check scroll status for content that requires scrolling
    if (!scrollStatus.left || !scrollStatus.right) {
      setShowScrollWarning(true);
      return;
    }

    handleSaveAnswer();

    if (state.currentQuestionIndex === section.questions.length - 1) {
      if (isReviewState) {
        // If already in review mode and clicked next on last question, just stay here or show end section?
        // Usually Pearson stays on the review screen until "End Review" is clicked.
        setIsReviewState(true);
        setHasReachedReview(true);
      } else {
        setIsReviewState(true);
        setHasReachedReview(true);
      }
    } else {
      dispatchReducer({ type: "NEXT_QUESTION" });
    }
  };

  const submitExam = (forceEnd: boolean = false) => {
    // Flag as submitting to bypass the navigation blocker
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    setShowEndSection(false);
    handleSaveAnswer();
    if (studentExamId && section.id) {
      console.log("Triggering addCloseSection:", { student_exam_id: studentExamId, question_type_id: section.id });
      dispatch(
        addCloseSection({
          student_exam_id: studentExamId,
          remaining_time: formatTime(state.timeRemaining),
          closed: true,
          question_type_id: section.id,
        })
      );
      dispatch(listExamID({ stud_exam_id: studentExamId }));

      // Clear persistent timer for the current section
      const timerKey = `ucat_timer_${studentExamId}_${effectiveSectionIndex}`;
      sessionStorage.removeItem(timerKey);
    }

    if (!isLastSection && !forceEnd) {
      // Find the next section in the current rawSections list
      const currentIdx = rawSections.findIndex((s: any) => s.question_type_id === rawSection?.question_type_id);
      const nextSection = rawSections[currentIdx + 1];

      // 👉 Move to instruction screen for the next section
      navigate("/ucat_exampractice_instruction", {
        replace: true,
        state: {
          sectionIndex: effectiveSectionIndex + 1,
          exam_id: location.state?.exam_id || sessionStorage.getItem("ucat_exam_id"),
          question_type_id: nextSection?.question_type_id,
          resetTimer: true,
        },
      });

      dispatchReducer({ type: "JUMP_QUESTION", index: 0 });
      return;
    }

    // 👉 FINAL SUBMISSION - Clear stale session data
    sessionStorage.removeItem("ucat_last_section_index");
    sessionStorage.removeItem("ucat_last_section_id");
    sessionStorage.removeItem("ucat_exam_id");
    if (studentExamId) {
      dispatch(listExamsubmit({ id: studentExamId }));
    }

    // 👉 FINAL RESULT (all sections completed)
    const questionsByTypeMap: Record<string, { count: number; incorrect: number }> = {};
    let total = 0;
    let correct = 0;

    section.questions.forEach((q: any) => {
      const userAnswer = state.answers[q.question_id];
      const isCorrect = typeof q.correctAnswerIndex === "number" && userAnswer?.selectedOptionIndex === q.correctAnswerIndex;

      total++;
      if (isCorrect) correct++;

      const typeName = q.subtype_name || section.title || "General";
      if (!questionsByTypeMap[typeName]) {
        questionsByTypeMap[typeName] = { count: 0, incorrect: 0 };
      }
      questionsByTypeMap[typeName].count++;
      if (!isCorrect) {
        questionsByTypeMap[typeName].incorrect++;
      }
    });

    const questionsByType = Object.entries(questionsByTypeMap).map(([name, stats]) => ({
      name,
      ...stats
    }));

    setIsSubmitting(true); // Allow navigation to summary page
    navigate("/ucat_testevaluation", {
      state: {
        total,
        correct,
        sectionTitle: section.title,
        questionsByType,
        studentExamId: studentExamId,
        evalAs: "exam",
      },
    });
  };

  useEffect(() => {
    console.warn("navigator state", showNavigator);
  }, [showNavigator]);

  // // Security measures to block inspection tools
  // useEffect(() => {
  //   const handleContextMenu = (e: MouseEvent) => {
  //     e.preventDefault();
  //   };

  //   const handleKeyDownSecurity = (e: KeyboardEvent) => {
  //     // Block F12
  //     if (e.key === "F12") {
  //       e.preventDefault();
  //       return;
  //     }

  //     // Block Ctrl+Shift+I, Ctrl+Shift+J, Ctrl+Shift+C (Cmd+Option+I/J/C on Mac)
  //     if (
  //       (e.ctrlKey || e.metaKey) &&
  //       e.shiftKey &&
  //       (e.key === "I" || e.key === "i" || e.key === "J" || e.key === "j" || e.key === "C" || e.key === "c")
  //     ) {
  //       e.preventDefault();
  //       return;
  //     }

  //     // Block Ctrl+U (View Source)
  //     if ((e.ctrlKey || e.metaKey) && (e.key === "U" || e.key === "u")) {
  //       e.preventDefault();
  //       return;
  //     }
  //   };

  //   window.addEventListener("contextmenu", handleContextMenu);
  //   window.addEventListener("keydown", handleKeyDownSecurity, true); // Use capture to intercept early

  //   return () => {
  //     window.removeEventListener("contextmenu", handleContextMenu);
  //     window.removeEventListener("keydown", handleKeyDownSecurity, true);
  //   };
  // }, []);

  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Don't show the warning if we are intentionally submitting/navigating
      if (isSubmitting || isSubmittingRef.current) return;

      // Standard approach to trigger browser's confirmation dialog
      e.preventDefault();
      e.returnValue = "";
    };

    // Intercept Keyboard Reloads and Add Pearson Shortcuts
    const handleKeyDown = (e: KeyboardEvent) => {
      // Check for F5 or Ctrl+R / Cmd+R
      if (
        e.key === "F5" ||
        ((e.ctrlKey || e.metaKey) && (e.key === "r" || e.key === "R"))
      ) {
        // Prevent default browser reload
        e.preventDefault();
        e.stopPropagation(); // Stop bubbling
        setShowReloadWarning(true);
        return;
      }

      // Esc to close popups (should work even when typing)
      if (e.key === "Escape") {
        if (showNavigator) {
          setShowNavigator(false);
          e.preventDefault();
        } else if (showCalculator) {
          setShowCalculator(false);
          e.preventDefault();
        } else if (showEndExamConfirmation) {
          setShowEndExamConfirmation(false);
          e.preventDefault();
        } else if (showScrollWarning) {
          setShowScrollWarning(false);
          e.preventDefault();
        } else if (showEndSection) {
          setShowEndSection(false);
          e.preventDefault();
        } else if (showEndReview) {
          setShowEndReview(false);
          e.preventDefault();
        } else if (showReloadWarning) {
          setShowReloadWarning(false);
          e.preventDefault();
        } else if (showNoFlaggedPopup) {
          setShowNoFlaggedPopup(false);
          e.preventDefault();
        }
      }

      // Pearson Shortcuts (only when not typing)
      const target = e.target as HTMLElement;
      const isTyping =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable ||
        target.closest(".ql-editor") !== null;

      if (isTyping) return;

      // Alt + N (Next), Alt + P (Previous), Alt + F (Flag), Alt + E (End Exam), Alt + C (Calculator), Alt + V (Navigator)
      if (e.altKey) {
        // Use e.code to consistently detect keys even when Option is pressed on Mac (which changes e.key)
        const code = e.code;

        if (showEndSection) {
          e.preventDefault();

          if (code === "KeyY") {
            submitExam(false);
          } else if (code === "KeyN") {
            setShowEndSection(false);
          }
        } else if (showScrollWarning) {
          e.preventDefault();
          if (code === "KeyO") {
            setShowScrollWarning(false);
          }
        } else if (showReloadWarning) {
          // Handle ReloadWarningModal shortcuts
          if (code === "KeyR") {
            e.preventDefault();
            handleReloadConfirm();
          } else if (code === "KeyC") {
            e.preventDefault();
            handleReloadCancel();
          }
        } else if (showNoFlaggedPopup) {
          e.preventDefault();
          if (code === "KeyO") {
            setShowNoFlaggedPopup(false);
          }
        } else if (showNavigator) {
          e.preventDefault();

          if (code === "KeyC") {
            setShowNavigator(false);
          }
        } else if (code === "KeyN") {
          e.preventDefault();
          onNext();
        } else if (code === "KeyP") {
          e.preventDefault();
          if (state.currentQuestionIndex > 0) {
            handleSaveAnswer();
            dispatchReducer({ type: "PREV_QUESTION" });
          }
        } else if (code === "KeyF") {
          e.preventDefault();
          handleFlag();
        } else if (code === "KeyE") {
          e.preventDefault();
          if (isReviewState) {
            setShowEndReview(true);
          } else {
            setShowEndExamConfirmation(true);
          }
        } else if (code === "KeyC") {
          e.preventDefault();
          setShowCalculator(true);
        } else if (code === "KeyV") {
          e.preventDefault();
          if (isReviewState) {
            const firstFlagged = section.questions.findIndex((q: any) => {
              const ans = state.answers[q.question_id];
              return ans?.isFlagged;
            });
            if (firstFlagged !== -1) {
              dispatchReducer({ type: "JUMP_QUESTION", index: firstFlagged });
              setIsReviewState(false);
            }
            else {
              showNotFlaggedPopup()
            }
          } else {
            setShowNavigator(true);
          }
        }
      }

      // Alt+Y/Alt+N for SubmitExamModal
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    // Attach keydown to document with useCapture=true to intercept before anyone else
    document.addEventListener("keydown", handleKeyDown, true);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("keydown", handleKeyDown, true);
    };
  }, [
    onNext,
    handleFlag,
    handleSaveAnswer,
    state,
    setShowReloadWarning,
    showNavigator,
    showEndExamConfirmation,
    showScrollWarning,
    showCalculator,
    showEndSection,
    showEndReview,
    showReloadWarning,
    isReviewState,
    section,
    submitExam,
    handleReloadConfirm,
    handleReloadCancel,
    showNoFlaggedPopup,
    setShowNoFlaggedPopup
  ]);

  // --- Calculate Counts for Submit Modal ---
  const unansweredCount = section.questions.filter((q: any) => {
    const ans = state.answers[q.question_id];
    const isMcqAnswered = ans && ans.selectedOptionIndex !== undefined && ans.selectedOptionIndex !== null;
    const isDragDropAnswered = ans && ans.dragDropAnswers && Object.values(ans.dragDropAnswers).some(val => val !== null);
    const isOtherAnswered = ans && (
      (ans.sentence && ans.sentence.trim() !== "" && ans.sentence !== "null" && ans.sentence !== "{}") ||
      (ans.answer_data && ans.answer_data.trim() !== "" && ans.answer_data !== "null" && ans.answer_data !== "{}")
    );
    return !isMcqAnswered && !isDragDropAnswered && !isOtherAnswered;
  }).length;

  const flaggedCount = Object.values(state.answers).filter((ans: any) => ans.isFlagged).length;

  const question: Question = section.questions[state.currentQuestionIndex];

  useEffect(() => {
    if (question?.question_id) {
      setQuestionStartTime(new Date().toISOString());
      dispatchReducer({ type: "VIEW_QUESTION", questionId: question.question_id });
    }
  }, [question?.question_id]);

  const userAnswer = state.answers[question?.question_id] ?? {
    selectedOptionIndex: null,
    dragDropAnswers: {},
    isFlagged: false,
  };

  const isAnyLoading = isLoading || isPendingExamLoading || isReExamLoading || isSkipExamLoading;

  // Retry Logic State
  const [isRetrying, setIsRetrying] = useState(true);

  useEffect(() => {
    if (section.questions && section.questions.length > 0) {
      setIsRetrying(false);
      return;
    }

    if (!isAnyLoading && (!section.questions || section.questions.length === 0)) {
      console.log("⚠️ No questions found. Retrying for 5s...");
      const timer = setTimeout(() => {
        setIsRetrying(false);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [section.questions?.length, isAnyLoading]);

  // If data is loading or missing, or if we are in the retry window
  const shouldShowLoader = !examData ||
    ((isAnyLoading || isRetrying) && (!section.questions || section.questions.length === 0));

  if (shouldShowLoader) {
    return (
      <div className="flex h-screen items-center justify-center bg-white flex-col gap-3">
        <CircularProgress />
        <p className="text-gray-500 font-medium">{isRetrying && !isAnyLoading ? "Retrying..." : "Loading Exam..."}</p>
      </div>
    );
  }

  if (!section.questions.length) {
    return (
      <div className="flex flex-col items-center justify-center p-10 h-screen">
        <div className="p-6 bg-red-50 rounded-lg border border-red-100 text-center">
          <h3 className="text-lg font-semibold text-red-600 mb-2">No Questions Found</h3>
          <p className="text-gray-600">We couldn't load questions for this section.</p>
          <button
            onClick={() => window.location.reload()}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
          >
            Reload Page
          </button>
        </div>
      </div>
    );
  }

  // Guard against question being undefined if index is out of bounds (though shouldn't happen)
  if (!question) {
    return <div className="p-10">Error loading question.</div>;
  }

  const isLastSection =
    rawSection?.question_type_id === rawSections[rawSections.length - 1]?.question_type_id;

  const hasPassage =
    (question.prompt && question.prompt.trim() !== "") ||
    (question.instruction && question.instruction.trim() !== "") ||
    (question.prompt_url && question.prompt_url.trim() !== "");

  // Determine if we should use split layout based on question type
  const shouldUseSplitLayout =
    section.title === "Verbal Reasoning" ||
    section.title === "Reading Comprehension" ||
    (section.title === "Situational Judgement" && question.answer_type !== AnswerType.DRAG_DROP) ||
    (section.title === "Decision Making" && (question.prompt_url && question.prompt_url.trim() !== "")) ||
    (section.title === "Quantitative Reasoning" && (question.prompt_url && question.prompt_url.trim() !== ""));

  const sectionsForNavigator: any[] = rawSections.map(
    (s: any) => ({
      id: s.question_type_id,
      title: s.question_type_name,
      questions: normalizeQuestions(s.questions),
    })
  );

  return (
    <UcatLayout
      headerTitle={examData?.exam_name || section.title || "UCAT Practice"}
      headerSubtitle={!isReviewState && section.questions.length > 0 ? `${state.currentQuestionIndex + 1} of ${section.questions.length}` : ""}
      showToolbar={true}
      showClearFlagButton={isReviewState}
      onClearFlagClick={handleClearAllFlags}
      toolbarRightActions={
        !isReviewState ? (
          <button
            onClick={handleFlag}
            className="flex items-center gap-1.5 text-white hover:text-[#FFFF00] px-3 h-full transition-all duration-200 group"
          >
            <Flag
              size={18}
              className={
                userAnswer?.isFlagged
                  ? "fill-[#FFFF00] text-[#FFFF00] drop-shadow-[0_0_8px_rgba(255,255,0,0.5)]"
                  : "text-white/90 group-hover:scale-110 transition-transform"
              }
            />
            <span className="text-[16px] font-medium tracking-tight">
              <span className="underline decoration-[1.5px] text-[16px] underline-offset-[3px]">F</span>{userAnswer.isFlagged ? "lagged" : "lag"} for Review
            </span>
          </button>
        ) : null
      }
      toolbarLeftActions={
        isReviewState && (
          <button
            onClick={() => setShowInstructions(true)}
            className="flex items-center gap-2 text-white hover:text-[#FFFF00] px-3 h-full transition-all duration-200 group"
          >
            <PenLine size={16} />
            <span className="text-[19px] font-normal">
              Inst<span style={{ textDecoration: 'underline', fontSize: "inherit", fontWeight: "inherit" }}>r</span>uctions
            </span>
          </button>
        )
      }
      showCalculatorButton={!isReviewState}
      onCalculatorClick={() => setShowCalculator(true)}
      timerValue={rawSection?.duration ? formatTime(state.timeRemaining) : undefined}
      timerLow={state.timeRemaining < 180 && state.timeRemaining > 0}
      footerProps={!isReviewState ? {
        onNext: onNext,
        onPrevious: state.currentQuestionIndex > 0 ? () => {
          handleSaveAnswer();
          dispatchReducer({ type: "PREV_QUESTION" });
        } : undefined,
        onNavigator: () => setShowNavigator(true),
        onEndExam: hasReachedReview ? () => {
          handleSaveAnswer();
          setIsReviewState(true);
        } : () => setShowEndExamConfirmation(true),
        showNavigator: !hasReachedReview,
        showReview: false,
        showPrevious: state.currentQuestionIndex > 0,
        nextDisabled: false,
        previousDisabled: false,
        leftButtonText: hasReachedReview ? "Return to Review" : "",
        rightButtonText: state.currentQuestionIndex === section.questions.length - 1 ? "End Section" : "Next"
      } : undefined}
    >

      {isReviewState ? (
        <UcatSectionReview
          sectionTitle={section.title}
          questions={section.questions}
          answers={state.answers}
          isLastSection={isLastSection}
          showInstructions={showInstructions}
          setShowInstructions={setShowInstructions}
          onJump={(idx) => {
            dispatchReducer({ type: "JUMP_QUESTION", index: idx });
            setIsReviewState(false);
          }}
          onReviewAll={() => {
            dispatchReducer({ type: "JUMP_QUESTION", index: 0 });
            setIsReviewState(false);
          }}
          onReviewIncomplete={() => {
            const firstIncomplete = section.questions.findIndex((q: any) => {
              const ans = state.answers[q.question_id];
              const isMcqAnswered = ans && (ans.selectedOptionIndex !== null && ans.selectedOptionIndex !== undefined);
              const isDragDropAnswered = ans && ans.dragDropAnswers && Object.values(ans.dragDropAnswers).some(val => val !== null);
              const isOtherAnswered = ans && (
                (ans.sentence && ans.sentence.trim() !== "" && ans.sentence !== "null" && ans.sentence !== "{}") ||
                (ans.answer_data && ans.answer_data.trim() !== "" && ans.answer_data !== "null" && ans.answer_data !== "{}")
              );
              return !isMcqAnswered && !isDragDropAnswered && !isOtherAnswered;
            });
            if (firstIncomplete !== -1) {
              dispatchReducer({ type: "JUMP_QUESTION", index: firstIncomplete });
              setIsReviewState(false);
            }
          }}
          onReviewFlagged={() => {
            const firstFlagged = section.questions.findIndex((q: any) => {
              const ans = state.answers[q.question_id];
              return ans?.isFlagged;
            });
            if (firstFlagged !== -1) {
              dispatchReducer({ type: "JUMP_QUESTION", index: firstFlagged });
              setIsReviewState(false);
            } else {
              setShowNoFlaggedPopup(true);
            }
          }}
          onEndReview={() => setShowEndReview(true)}
          showEndReview={showEndReview}
          onEndReviewConfirm={() => {
            setShowEndReview(false);
            submitExam(false);
          }}
          onEndReviewCancel={() => setShowEndReview(false)}
          onToggleFlag={handleToggleFlag}
        />
      ) : (
        /* Question */
        <div className="flex-1 overflow-hidden min-h-0 ucat-question-body">
          {section.title === "Writing" || section.title?.toLowerCase().includes("writing") ? (
            <div className="flex h-full min-h-0">
              {/* Left Side - Prompt/Image */}
              <div
                ref={writingLeftScrollRef}
                onScroll={checkWritingScroll}
                className="w-1/2 h-full overflow-y-auto p-6 bg-white border-r border-gray-200 min-h-0"
              >
                {/* Creative Writing Header */}
                <div className="text-white px-4 py-2 rounded-lg mb-4 font-medium" style={{ backgroundColor: '#003b70' }}>
                  Creative Writing
                </div>

                {/* Instruction Text */}
                <p className="text-gray-700 mb-4 text-sm">
                  Use this idea as the basis for a piece of writing. You may write in any style.
                </p>

                {/* Question Image */}
                {question.question_url && (
                  <div className="mb-4">
                    <img
                      src={question.question_url}
                      alt="Writing Prompt"
                      className="w-full h-auto rounded-lg shadow-md"
                    />
                  </div>
                )}

                {/* Instruction Box - Blue icon box on left, text on right */}
                <div className="flex items-start gap-3 mb-6">
                  <div className="w-full max-w-[60px] p-3 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#003b70' }}>
                    <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  </div>
                  <p className="text-sm text-gray-700 leading-relaxed flex-1 pt-2">
                    {question.instruction || "Look at the image above and write a creative piece inspired by what you see. You may choose any writing style - narrative, descriptive, persuasive, or analytical."}
                  </p>
                </div>

                {/* Word Count Statistics - Horizontal Row */}
                <div className="flex justify-between gap-4 mb-6">
                  <div className="flex-1 text-center">
                    <div className="text-4xl font-bold text-gray-800 mb-1">{wordCount}</div>
                    <div className="text-xs text-gray-600">Words</div>
                  </div>
                  <div className="flex-1 text-center">
                    <div className="text-4xl font-bold text-gray-800 mb-1">{characterCount}</div>
                    <div className="text-xs text-gray-600">Characters</div>
                  </div>
                  <div className="flex-1 text-center">
                    <div className="text-4xl font-bold text-gray-800 mb-1">{sentenceCount}</div>
                    <div className="text-xs text-gray-600">Sentences</div>
                  </div>
                  <div className="flex-1 text-center">
                    <div className="text-4xl font-bold text-gray-800 mb-1">{paragraphCount}</div>
                    <div className="text-xs text-gray-600">Paragraphs</div>
                  </div>
                </div>

                {/* Word Count Warning */}
                {wordCount < 200 && (
                  <div className="bg-orange-50 text-orange-600 p-3 rounded-lg text-center text-sm font-medium">
                    Write between 200–300 words
                  </div>
                )}
              </div>

              {/* Right Side - Text Editor */}
              <div
                ref={writingRightScrollRef}
                onScroll={checkWritingScroll}
                className="w-1/2 h-full overflow-y-auto p-6 bg-gray-50 min-h-0"
              >
                <div className="mb-2">
                  <h3 className="text-sm font-medium text-gray-700 mb-2">Your Response:</h3>
                </div>

                {/* ReactQuill Editor */}
                <ReactQuill
                  className="custom-quill bg-white"
                  style={{
                    width: "100%",
                    height: "calc(100vh - 300px)",
                    borderRadius: "8px",
                  }}
                  theme="snow"
                  value={editorValue}
                  onChange={handleEditorChange}
                  modules={modules}
                  formats={formats}
                  placeholder="Start writing here..."
                  preserveWhitespace
                  data-gramm="false"
                  onFocus={() => {
                    const editorElement = document.querySelector(".ql-editor");
                    if (editorElement) {
                      editorElement.setAttribute("spellcheck", "false");
                      editorElement.setAttribute("autocorrect", "off");
                      editorElement.setAttribute("autocapitalize", "off");
                      editorElement.setAttribute("data-gramm", "false");
                      (editorElement as HTMLElement).style.fontSize = "14px";
                    }
                  }}
                />
              </div>
            </div>
          ) : shouldUseSplitLayout ? (
            <SplitPane
              key={question.question_id}
              passage={question.prompt || question.instruction || undefined}
              promptUrl={question.prompt_url || undefined}
              question={question}
              selectedOptionIndex={userAnswer.selectedOptionIndex}
              dragDropAnswers={userAnswer.dragDropAnswers}
              onOptionSelect={handleOptionSelect}
              onDragDropChange={handleDragDropChange}
              onScrollStatusChange={setScrollStatus}
              onLockedClick={() => setShowScrollWarning(true)}
            />
          ) : (
            <SinglePageQuestion
              key={question.question_id}
              question={question}
              selectedOptionIndex={userAnswer.selectedOptionIndex}
              dragDropAnswers={userAnswer.dragDropAnswers}
              passage={question.prompt || question.instruction || undefined}
              promptUrl={question.prompt_url || undefined}
              onOptionSelect={handleOptionSelect}
              onDragDropChange={handleDragDropChange}
              onScrollStatusChange={setScrollStatus}
              onLockedClick={() => setShowScrollWarning(true)}
            />
          )}
        </div>
      )}

      {/* Modals */}
      <NavigatorModal
        isOpen={showNavigator}
        onClose={() => setShowNavigator(false)}
        sections={[section]}
        currentSectionIndex={0}
        currentQuestionIndex={state.currentQuestionIndex}
        answers={state.answers}
        onJump={(sIdx, qIdx) => {
          if (!scrollStatus.left || !scrollStatus.right) {
            setShowNavigator(false);
            setShowScrollWarning(true);
            return;
          }
          handleSaveAnswer();
          dispatchReducer({ type: "JUMP_QUESTION", index: qIdx });
          setShowNavigator(false);
        }}
      />

      <SubmitExamModal
        isOpen={showEndSection}
        onClose={() => setShowEndSection(false)}
        onConfirm={() => submitExam(false)}
        unansweredCount={unansweredCount}
        flaggedCount={flaggedCount}
      />

      <EndReviewConfirmationModal
        isOpen={showEndReview}
        onClose={() => setShowEndReview(false)}
        onConfirm={() => {
          setShowEndReview(false);
          submitExam(false);
        }}
        incompleteCount={unansweredCount}
      />

      <TimeExpiredModal
        isOpen={showTimeExpiredModal}
        onConfirm={() => {
          setShowTimeExpiredModal(false);
          submitExam(false);
        }}
      />

      <ScrollWarningModal
        isOpen={showScrollWarning}
        onClose={() => setShowScrollWarning(false)}
      />

      <Calculator
        isOpen={showCalculator}
        onClose={() => setShowCalculator(false)}
      />

      <ReloadWarningModal
        isOpen={showReloadWarning}
        onClose={handleReloadCancel}
        onConfirm={handleReloadConfirm}
        isLoading={isSkipExamLoading}
      />

      <EndExamConfirmationModal
        isOpen={showEndExamConfirmation}
        onClose={() => setShowEndExamConfirmation(false)}
        onConfirm={async () => {
          setShowEndExamConfirmation(false);
          await handleSaveAnswer();
          await handleSkipExam();
        }}
      />

      <NoFlaggedModal
        isOpen={showNoFlaggedPopup}
        onClose={() => setShowNoFlaggedPopup(false)}
      />
    </UcatLayout>
  );
};

export default UcatPracticeExam;